import { useCallback, useEffect, useMemo } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type { LexProduct } from '@/shared/hooks/api/product'

export type NewChatMode = 'general' | 'product'

type UseLlmChatDraftStateInput = {
  searchParams: URLSearchParams
  navigate: NavigateFunction
  sessionId: string | undefined
  isNewDraft: boolean
  products: LexProduct[]
  productsLoaded: boolean
}

type SetDraftParamsInput = {
  mode?: NewChatMode | null
  productId?: string | null
}

export function useLlmChatDraftState(input: UseLlmChatDraftStateInput) {
  const draftModeParam = input.searchParams.get('mode')
  const draftMode: NewChatMode | null =
    draftModeParam === 'general' || draftModeParam === 'product'
      ? draftModeParam
      : null

  const draftProductIdParam = input.searchParams.get('productId')
  const draftProductId =
    draftProductIdParam != null && draftProductIdParam.trim() !== ''
      ? draftProductIdParam.trim()
      : null

  const selectedDraftProduct = useMemo(
    () =>
      draftProductId != null
        ? input.products.find((product) => product.id === draftProductId) ?? null
        : null,
    [draftProductId, input.products]
  )

  const setDraftParams = useCallback(
    (next: SetDraftParamsInput) => {
      const nextParams = new URLSearchParams()
      nextParams.set('new', '1')
      if (next.mode != null) {
        nextParams.set('mode', next.mode)
      }
      if (next.productId != null && next.productId.trim() !== '') {
        nextParams.set('productId', next.productId.trim())
      }
      input.navigate(`/llm-chat?${nextParams.toString()}`, {
        replace: true,
      })
    },
    [input]
  )

  useEffect(() => {
    if (!input.isNewDraft || input.sessionId) {
      return
    }
    if (draftMode === 'product' && draftProductId != null && input.productsLoaded) {
      const hasProduct = input.products.some(
        (product) => product.id === draftProductId
      )
      if (!hasProduct) {
        setDraftParams({
          mode: 'product',
          productId: null,
        })
      }
    }
  }, [
    draftMode,
    draftProductId,
    input.isNewDraft,
    input.products,
    input.productsLoaded,
    input.sessionId,
    setDraftParams,
  ])

  const productDraftCanCompose =
    draftMode === 'product' &&
    draftProductId != null &&
    selectedDraftProduct != null
  const generalDraftCanCompose = draftMode === 'general'
  const canComposeDraft = generalDraftCanCompose || productDraftCanCompose

  return {
    draftMode,
    draftProductId,
    selectedDraftProduct,
    setDraftParams,
    productDraftCanCompose,
    canComposeDraft,
  }
}
